import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { cn, getInitials } from '../../lib/utils';
import { Hash, AtSign, X } from 'lucide-react';

// MentionInput component with autocomplete for @user and #channel mentions
const MentionInput = forwardRef(({
    value,
    onChange,
    onKeyPress,
    onPaste,
    onDragOver,
    onDrop,
    placeholder,
    className,
    members = [],
    channels = [],
    disabled = false,
    rows = 1,
}, ref) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionType, setSuggestionType] = useState(null); // 'user' or 'channel'
    const [suggestions, setSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const textareaRef = useRef(null);
    const suggestionsRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focus: () => textareaRef.current?.focus(),
        get value() { return textareaRef.current?.value; },
    }));

    // Check for mention trigger (@, #) when typing
    const checkForMentionTrigger = (text, position) => {
        // Find the word being typed (from last space or start to cursor)
        let startIndex = position - 1;
        while (startIndex >= 0 && text[startIndex] !== ' ' && text[startIndex] !== '\n') {
            startIndex--;
        }
        startIndex++;

        const word = text.slice(startIndex, position);

        if (word.startsWith('@')) {
            const search = word.slice(1).toLowerCase();
            setSearchTerm(search);
            setSuggestionType('user');
            
            const filtered = members.filter(m => {
                const name = (m.user_name || m.name || '').toLowerCase();
                const email = (m.user_email || m.email || '').toLowerCase();
                return name.includes(search) || email.includes(search);
            }).slice(0, 5);
            
            setSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
            setSelectedIndex(0);
            return { type: 'user', startIndex, search };
        } else if (word.startsWith('#')) {
            const search = word.slice(1).toLowerCase();
            setSearchTerm(search);
            setSuggestionType('channel');
            
            const filtered = channels.filter(c => 
                c.name.toLowerCase().includes(search)
            ).slice(0, 5);
            
            setSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
            setSelectedIndex(0);
            return { type: 'channel', startIndex, search };
        } else {
            setShowSuggestions(false);
            setSuggestions([]);
            return null;
        }
    };

    const handleChange = (e) => {
        const text = e.target.value;
        const position = e.target.selectionStart;
        setCursorPosition(position);
        onChange(e);
        checkForMentionTrigger(text, position);
    };

    const handleKeyDown = (e) => {
        if (showSuggestions && suggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev => (prev + 1) % suggestions.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(suggestions[selectedIndex]);
            } else if (e.key === 'Escape') {
                setShowSuggestions(false);
            }
        } else if (e.key === 'Enter' && !e.shiftKey) {
            onKeyPress && onKeyPress(e);
        }
    };

    const insertMention = (item) => {
        const text = value;
        
        // Find the start of the current word (@ or #)
        let startIndex = cursorPosition - 1;
        while (startIndex >= 0 && text[startIndex] !== ' ' && text[startIndex] !== '\n') {
            startIndex--;
        }
        startIndex++;

        let insertText = '';
        if (suggestionType === 'user') {
            // Insert @username format
            const name = item.user_name || item.name || 'user';
            insertText = `@${name.replace(/\s+/g, '_')} `;
        } else if (suggestionType === 'channel') {
            // Insert #channel format
            insertText = `#${item.name} `;
        }

        const newText = text.slice(0, startIndex) + insertText + text.slice(cursorPosition);
        
        // Update the value
        const fakeEvent = {
            target: {
                value: newText,
                selectionStart: startIndex + insertText.length,
                selectionEnd: startIndex + insertText.length,
            }
        };
        onChange(fakeEvent);
        
        // Close suggestions
        setShowSuggestions(false);
        setSuggestions([]);
        
        // Focus and set cursor position
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(
                    startIndex + insertText.length,
                    startIndex + insertText.length
                );
            }
        }, 0);
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative w-full">
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={onPaste}
                onDragOver={onDragOver}
                onDrop={onDrop}
                placeholder={placeholder}
                data-testid="mention-input-textarea"
                className={cn(
                    'flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                    className
                )}
                disabled={disabled}
                rows={rows}
            />

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div
                    ref={suggestionsRef}
                    data-testid="mention-suggestions-dropdown"
                    className="absolute bottom-full mb-1 left-0 w-full max-w-sm bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
                >
                    <div className="px-3 py-2 border-b border-gray-100 text-xs text-gray-500">
                        {suggestionType === 'user' ? (
                            <span className="flex items-center gap-1">
                                <AtSign className="w-3 h-3" />
                                Mention user
                            </span>
                        ) : (
                            <span className="flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                Link channel
                            </span>
                        )}
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        {suggestions.map((item, index) => (
                            <button
                                key={suggestionType === 'user' ? item.user_id : item.id}
                                onClick={() => insertMention(item)}
                                className={cn(
                                    'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors',
                                    index === selectedIndex && 'bg-gray-100'
                                )}
                            >
                                {suggestionType === 'user' ? (
                                    <>
                                        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                                            {getInitials(item.user_name || item.name || 'U')}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">
                                                {item.user_name || item.name}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {item.user_email || item.email}
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Hash className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">
                                                {item.name}
                                            </p>
                                            {item.topic && (
                                                <p className="text-xs text-gray-500 truncate">
                                                    {item.topic}
                                                </p>
                                            )}
                                        </div>
                                    </>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});

MentionInput.displayName = 'MentionInput';

export default MentionInput;
